import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { uniqueId } from 'lodash-es';
import { useQuery } from 'react-query';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { ScheduleRequestsApi, ScheduleTypes, ScheduleDefaults } from '@frontend/api-schedule';
import { useTranslation } from '@frontend/i18n';
import { ScopedSingleLocationFilterMenu } from '@frontend/location-filter-menu';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Tabs, useModalControl, useAlert, useFormField } from '@frontend/design-system';
import { CustomFieldEditor } from '../../components/CustomFieldsEditor/CustomFieldEditor';
import { useSchedulingLocationInfo } from '../../hooks';
import { getBookingSiteFieldDefaults } from '../../utils';
import { CustomFieldsButtonFooter } from './CustomFieldsButtonFooter';
import { CustomFieldsIllustrationContainer } from './CustomFieldsIllustrationContainer';
import { FieldList } from './FieldList';
import { PreviewForm } from './PreviewForm';

enum TabsEnum {
  FieldList = 'field-list-tab',
  PreviewForm = 'preview-form-tab',
}

export function CustomFields() {
  const { t } = useTranslation('schedule');
  const { success, warning } = useAlert();
  const isMedium = useMatchMedia({ minWidth: breakpoints.medium.min });
  const { selectedLocationIds, getCFLocationMap } = useSchedulingLocationInfo();
  const { customizationFlags } = useAppFlagStore();

  const onlineSchedulingCFEnabledLocationIds = useMemo(() => {
    const onlineSchedulingCFLocationMap = getCFLocationMap('onlinescheduling');

    const onlineSchedulingCFEnabledLocations =
      Object.keys(onlineSchedulingCFLocationMap).filter((locationId) => !!onlineSchedulingCFLocationMap[locationId]) ||
      [];
    return onlineSchedulingCFEnabledLocations;
    // NOTE: this is workaround for the issue where the customization flags are not immediately updated in the store when locations are changed
  }, [selectedLocationIds, customizationFlags, getCFLocationMap]);

  const selectedLocationField = useFormField(
    {
      type: 'checklist',
      value: onlineSchedulingCFEnabledLocationIds.length ? [onlineSchedulingCFEnabledLocationIds[0]] : [],
    },
    [onlineSchedulingCFEnabledLocationIds]
  );

  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  const overrideFieldDefaults = getBookingSiteFieldDefaults(isIntegratedOffice);

  const modalControl = useModalControl();

  const {
    data = {
      locationId: selectedLocationField.value[0],
      overrideFields: ScheduleDefaults.overrideFields,
      customFields: [],
    },
    refetch: refetchCustomFields,
  } = useQuery({
    queryKey: ['custom-fields', selectedLocationField.value[0]],
    enabled: !!selectedLocationField.value[0],
    queryFn: () => ScheduleRequestsApi.getCustomFields(selectedLocationField.value[0]),
    cacheTime: 5 * 60 * 1000, // 5 minutes
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data: ScheduleTypes.CustomFieldsResponse) => {
      const baseOverrideFields = data?.overrideFields || overrideFieldDefaults;

      const noteFieldWithDefaults = {
        ...overrideFieldDefaults.note,
        label: baseOverrideFields.note?.label ?? overrideFieldDefaults.note.label,
        show: baseOverrideFields.note?.show ?? overrideFieldDefaults.note.show,
        required: baseOverrideFields.note?.required ?? overrideFieldDefaults.note.required,
        requiredIsNotOptional: overrideFieldDefaults.note.requiredIsNotOptional,
        newUserOnly: baseOverrideFields.note?.newUserOnly ?? overrideFieldDefaults.note.newUserOnly,
      };

      let finalOverrideFields = {
        ...baseOverrideFields,
        note: noteFieldWithDefaults,
      };

      if (!isIntegratedOffice && baseOverrideFields.address) {
        const updatedSubFields = Object.entries(baseOverrideFields.address?.subFields || {}).reduce(
          (acc, [key, subField]) => {
            acc[key] = {
              ...subField,
              requiredIsNotOptional: false,
              required: subField.required,
            };
            return acc;
          },
          {} as Record<string, ScheduleTypes.Option>
        );

        finalOverrideFields = {
          ...finalOverrideFields,
          address: {
            ...overrideFieldDefaults.address,
            label:
              baseOverrideFields.address?.key === 'address'
                ? baseOverrideFields.address.label
                : overrideFieldDefaults.address.label,
            show: baseOverrideFields.address?.show,
            required: baseOverrideFields.address?.required,
            requiredIsNotOptional: false,
            subFields: updatedSubFields,
          },
        };
      }

      return {
        ...data,
        overrideFields: finalOverrideFields,
      };
    },
  });

  const [mode, setMode] = useState<'Create' | 'Update'>('Create');
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedField, setSelectedField] = useState<Partial<ScheduleTypes.CustomField>>({});

  // Editing state tracks changes that haven't been saved yet
  const [editedCustomFields, setEditedCustomFields] = useState<ScheduleTypes.CustomField[] | null>(null);
  const [editedOverrideFields, setEditedOverrideFields] = useState<Partial<
    Record<keyof ScheduleTypes.PatientInfo, ScheduleTypes.CustomField>
  > | null>(null);

  // Derive current working state: use edited versions if they exist, otherwise use query data
  const customFields = useMemo(
    () => editedCustomFields ?? (data?.customFields || []),
    [editedCustomFields, data?.customFields]
  );
  const overrideFields = useMemo(
    () => editedOverrideFields ?? (data?.overrideFields || overrideFieldDefaults),
    [editedOverrideFields, data?.overrideFields, overrideFieldDefaults]
  );

  // Manage preview form with localStorage
  const [previewForm, setPreviewFormState] = useState<Partial<ScheduleTypes.PatientInfo>>(() => {
    const formValuesJSON = localStorage.getItem('custom-fields-preview');
    return formValuesJSON ? JSON.parse(formValuesJSON) : {};
  });

  const setPreviewForm = useCallback((newForm: Partial<ScheduleTypes.PatientInfo>) => {
    setPreviewFormState(newForm);
    localStorage.setItem('custom-fields-preview', JSON.stringify(newForm));
  }, []);

  const saveData = useCallback(
    async ({
      selectedLocationId,
      dataToSave,
      customFieldsToSave,
      overrideFieldsToSave,
    }: {
      selectedLocationId: string;
      dataToSave: ScheduleTypes.CustomFieldsResponse;
      customFieldsToSave: ScheduleTypes.CustomField[];
      overrideFieldsToSave: Partial<Record<keyof ScheduleTypes.PatientInfo, ScheduleTypes.CustomField>>;
    }) => {
      try {
        setSaving(true);
        const response = await ScheduleRequestsApi.upsertCustomFields(selectedLocationId, {
          ...dataToSave,
          customFields: customFieldsToSave,
          overrideFields: overrideFieldsToSave,
        });
        setSaving(false);
        if (response?.locationId === selectedLocationId) {
          success({ message: t('Custom fields have been saved') });
        } else {
          warning({ message: t('Could not save custom fields. Please try again') });
        }
      } catch (error) {
        console.error('error while creating custom fields', error);
        warning({ message: t('Could not save custom fields. Please try again') });
      } finally {
        setSaving(false);
        refetchCustomFields();
      }
    },
    [t, success, warning, refetchCustomFields]
  );

  const selectedLocationId = selectedLocationField.value[0];

  const handleSave = useCallback(() => {
    if (selectedLocationId) {
      saveData({
        selectedLocationId,
        dataToSave: data,
        customFieldsToSave: customFields,
        overrideFieldsToSave: overrideFields,
      });
      // Clear edited state after save
      setEditedCustomFields(null);
      setEditedOverrideFields(null);
    }
  }, [selectedLocationId, data, customFields, overrideFields, saveData]);

  const handleReset = useCallback(() => {
    setEditedCustomFields(null);
    setEditedOverrideFields(null);
  }, []);

  const handleEditField = (field: ScheduleTypes.CustomField) => {
    setMode('Update');
    setSelectedField(field);
    modalControl.triggerProps.onClick();
  };

  const handleToggleShow = (field: ScheduleTypes.CustomField) => {
    if (field.custom) {
      handleUpdateField({ ...field, show: !field.show });
      return;
    }
    setEditedOverrideFields((fields) => {
      const currentFields = fields ?? (data?.overrideFields || overrideFieldDefaults);
      const updateFields = { ...currentFields };
      updateFields[field.key as keyof ScheduleTypes.PatientInfo] = { ...field, show: !field.show };
      return updateFields;
    });
  };

  const handleUpdateField = (field: ScheduleTypes.CustomField) => {
    if (field.custom) {
      setEditedCustomFields((fields) => {
        const currentFields = fields ?? (data?.customFields || []);
        const newFields = [...currentFields];
        const index = currentFields.findIndex((f) => f.id === field.id);
        if (index > -1) {
          newFields[index] = field;
        } else {
          newFields.push(field);
        }
        return newFields;
      });
    } else {
      setEditedOverrideFields((fields) => {
        const currentFields = fields ?? (data?.overrideFields || overrideFieldDefaults);
        return { ...currentFields, [field.key]: field };
      });
    }
  };

  const handleAddField = () => {
    setMode('Create');
    setSelectedField({
      id: uniqueId(),
      hideable: true,
      custom: true,
    });
    modalControl.triggerProps.onClick();
  };

  const handleDelete = (field: ScheduleTypes.CustomField) => {
    setEditedCustomFields((fields) => {
      const currentFields = fields ?? (data?.customFields || []);
      return currentFields.filter((f) => f.id !== field.id);
    });
  };

  const handleUpdateForm = (values: ScheduleTypes.PatientInfo) => {
    setPreviewForm({ ...previewForm, ...values });
  };

  const locationSelected = !!selectedLocationId;

  return (
    <>
      {isMedium ? (
        <div css={customFieldsFormContainerStyles}>
          {selectedLocationIds.length > 1 && (
            <div css={{ padding: theme.spacing(2, 3), borderBottom: `thin solid ${theme.colors.neutral20}` }}>
              <ScopedSingleLocationFilterMenu
                field={selectedLocationField}
                locationIds={onlineSchedulingCFEnabledLocationIds}
                css={locationDropdownStyle}
              />
            </div>
          )}
          {locationSelected ? (
            <>
              <div
                css={css({
                  display: 'flex',
                  width: '100%',
                })}
              >
                <div
                  css={css({
                    minWidth: 300,
                    width: `calc(40% - ${theme.spacing(3)})`,
                    padding: theme.spacing(3),
                    borderRight: `thin solid ${theme.colors.neutral20}`,
                  })}
                >
                  <FieldList
                    customFields={customFields}
                    overrideFields={overrideFields}
                    onAddField={handleAddField}
                    onToggleShowField={handleToggleShow}
                    onDelete={handleDelete}
                    onEditField={handleEditField}
                    isIntegrated={isIntegratedOffice}
                  />
                </div>
                <div css={css({ width: `calc(60% - ${theme.spacing(3)})`, padding: theme.spacing(2) })}>
                  <PreviewForm
                    form={previewForm}
                    onUpdateForm={handleUpdateForm}
                    customFields={customFields}
                    overrideFields={overrideFields}
                  />
                </div>
              </div>
              <CustomFieldsButtonFooter isSaving={saving} onSave={handleSave} onReset={handleReset} />
            </>
          ) : (
            <CustomFieldsIllustrationContainer />
          )}
        </div>
      ) : (
        <>
          {selectedLocationIds.length > 1 && (
            <div css={{ padding: theme.spacing(2, 0) }}>
              <ScopedSingleLocationFilterMenu
                field={selectedLocationField}
                locationIds={onlineSchedulingCFEnabledLocationIds}
                css={locationDropdownStyle}
              />
            </div>
          )}
          {locationSelected ? (
            <Tabs initialTab={TabsEnum.FieldList}>
              <Tabs.Bar>
                <Tabs.Tab id={TabsEnum.FieldList}>{t('Field List')}</Tabs.Tab>
                <Tabs.Tab id={TabsEnum.PreviewForm}>{t('Preview Form')}</Tabs.Tab>
              </Tabs.Bar>

              <Tabs.Panel controller={TabsEnum.FieldList} css={css({ padding: theme.spacing(3, 0) })}>
                <FieldList
                  key={selectedLocationField.value[0]}
                  customFields={customFields}
                  overrideFields={overrideFields}
                  onAddField={handleAddField}
                  onToggleShowField={handleToggleShow}
                  onDelete={handleDelete}
                  onEditField={handleEditField}
                  isIntegrated={isIntegratedOffice}
                />
              </Tabs.Panel>
              <Tabs.Panel controller={TabsEnum.PreviewForm} css={css({ padding: theme.spacing(3, 0) })}>
                <PreviewForm
                  key={selectedLocationField.value[0]}
                  form={previewForm}
                  onUpdateForm={handleUpdateForm}
                  customFields={customFields}
                  overrideFields={overrideFields}
                />
              </Tabs.Panel>
              <CustomFieldsButtonFooter isSaving={saving} onSave={handleSave} onReset={handleReset} />
            </Tabs>
          ) : (
            <div css={{ borderRadius: theme.spacing(1), boxShadow: theme.shadows.light, padding: theme.spacing(2) }}>
              <CustomFieldsIllustrationContainer />
            </div>
          )}
        </>
      )}

      <CustomFieldEditor mode={mode} modalControl={modalControl} field={selectedField} onChange={handleUpdateField} />
    </>
  );
}

const customFieldsFormContainerStyles = css({
  boxShadow: theme.shadows.light,
  borderRadius: theme.borderRadius.small,
  backgroundColor: theme.colors.white,
});

const locationDropdownStyle = css({
  background: theme.colors.primary50,
  color: theme.colors.white,
  p: { color: theme.colors.white },
});
